// ============================================================================
// FSA ROC Filing Pane — ADR-0027 MCA filing pack (AOC-4 set)
// ============================================================================
// Three steps, matching how the MCA V3 portal is used in offline mode:
//
//   1. Before download — the answers the portal asks ONLINE before it issues
//      the workbooks (FY, signatories, auditor, AGM, related-party Y/N …).
//      Several decide the workbook's shape, so they must be right first.
//   2. Workbook fields  — every OFFLINE field with PRIMA's value and where it
//      came from; anything PRIMA does not hold, or got wrong, is typed here.
//   3. Fill workbooks   — upload the portal's ZIP, download it filled. The user
//      then opens + saves each workbook in Excel and uploads it on the portal.
//
// PRIMA's values are read live from the saved FS and Corporate Facts; only
// what is typed here is stored (manual answers and overrides).

import React, { useCallback, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useFsaMcaFiling } from 'hooks/api/fsa/useFsaMcaFiling';
import { fsaMcaFilingService } from 'api/services/fsa/fsaMcaFilingService';
import type { McaManualValue, McaSheetField } from 'api/types/fsa/mcaFiling.types';

interface FsaMcaFilingPaneProps {
  engagementId: string;
}

type View = 'online' | 'offline' | 'fill';
type Filter = 'all' | 'blank' | 'typed';

const MAIN_FORM = 'AOC4';

const SOURCE_LABEL: Record<string, string> = {
  STATEMENT_LINE: 'FS',
  NOTE_CLASS: 'Note',
  FACT: 'Facts',
  SECTION: 'Facts',
  CONST: 'Fixed',
  MANUAL: 'Enter',
  ERROR: 'Error',
};

const keyOf = (f: McaSheetField) => `${f.formCode}|${f.target}`;

/** "requestBody.formData.balanceSheet.equityAndLiabilities…" → "Balance sheet". */
function groupOf(f: McaSheetField): string {
  const path = f.target.replace(/^requestBody\.formData\./, '');
  const first = path.split(/[.[]/)[0];
  if (f.sectionName) return humanize(f.sectionName);
  if (!path.includes('.')) return 'General';
  return humanize(first);
}

function humanize(s: string): string {
  const spaced = s.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function hasValue(f: McaSheetField): boolean {
  return f.value != null || (f.items?.some(i => i != null) ?? false);
}

const FsaMcaFilingPane: React.FC<FsaMcaFilingPaneProps> = ({ engagementId }) => {
  const { data: sheet, setData, loading, error, refetch } = useFsaMcaFiling(engagementId, MAIN_FORM);
  const [view, setView] = useState<View>('online');
  const [filter, setFilter] = useState<Filter>('all');
  const [drafts, setDrafts] = useState<Record<string, McaManualValue>>({});
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [confirmSave, setConfirmSave] = useState(false);
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [filling, setFilling] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const dirtyCount = Object.keys(drafts).length;

  const fieldsFor = useCallback((stage: 'ONLINE' | 'OFFLINE') => {
    if (!sheet) return [];
    return sheet.fields.filter(f => f.stage === stage).filter(f => {
      if (filter === 'blank') return !hasValue(f) && !drafts[keyOf(f)];
      if (filter === 'typed') return f.manualValue != null || f.manualItems != null || drafts[keyOf(f)] != null;
      return true;
    });
  }, [sheet, filter, drafts]);

  /** form → group → fields, in portal order. */
  const grouped = useMemo(() => {
    const stage = view === 'online' ? 'ONLINE' : 'OFFLINE';
    const result: Array<{ form: string; title: string; groups: Array<{ name: string; fields: McaSheetField[] }> }> = [];
    if (!sheet || view === 'fill') return result;
    for (const form of sheet.forms) {
      const own = fieldsFor(stage).filter(f => f.formCode === form.code);
      if (own.length === 0) continue;
      const groups: Array<{ name: string; fields: McaSheetField[] }> = [];
      for (const f of own) {
        const g = groupOf(f);
        const last = groups[groups.length - 1];
        if (last && last.name === g) last.fields.push(f);
        else groups.push({ name: g, fields: [f] });
      }
      result.push({ form: form.code, title: form.title, groups });
    }
    return result;
  }, [sheet, view, fieldsFor]);

  const setDraft = useCallback((f: McaSheetField, patch: McaManualValue) => {
    setDrafts(prev => ({ ...prev, [keyOf(f)]: { ...(prev[keyOf(f)] ?? { value: f.manualValue, items: f.manualItems, reason: f.reason }), ...patch } }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!sheet) return;
    setConfirmSave(false);
    setSaving(true);
    try {
      const values: Record<string, Record<string, McaManualValue | null>> = {};
      for (const [key, v] of Object.entries(drafts)) {
        const [form, target] = key.split('|');
        (values[form] ??= {})[target] = v;
      }
      const r = await fsaMcaFilingService.saveValues(engagementId, MAIN_FORM, { values, rowVersion: sheet.rowVersion });
      if (r.data) setData(r.data);
      setDrafts({});
      notificationService.toast.success('ROC filing values saved.');
    } catch (e: any) {
      const status = e?.response?.status;
      notificationService.toast.error(status === 409
        ? 'Someone else saved this filing meanwhile — reload, then re-enter your changes.'
        : (e?.response?.data?.message ?? 'Could not save the values.'), 6000);
    } finally {
      setSaving(false);
    }
  }, [sheet, drafts, engagementId, setData]);

  const handleFill = useCallback(async () => {
    if (files.length === 0) return;
    setFilling(true);
    try {
      await fsaMcaFilingService.fill(engagementId, MAIN_FORM, files);
      notificationService.toast.success('Filled workbooks downloaded — open each in Excel, review, save, then upload on the portal.');
      setFiles([]);
      if (fileInput.current) fileInput.current.value = '';
      refetch();
    } catch (e: any) {
      let message = 'Could not fill the workbooks.';
      const blob = e?.response?.data;
      if (blob instanceof Blob) {
        try { message = JSON.parse(await blob.text())?.message ?? message; } catch { /* keep default */ }
      }
      notificationService.toast.error(message, 6000);
    } finally {
      setFilling(false);
    }
  }, [files, engagementId, refetch]);

  const copy = useCallback((text: string | null | undefined) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(
      () => notificationService.toast.info('Copied.'),
      () => notificationService.toast.warning('Copy failed — select and copy manually.'));
  }, []);

  if (loading && !sheet) return <div style={styles.center}>Loading the ROC filing…</div>;
  if (error && !sheet) {
    return (
      <div style={styles.center}>
        <div>{(error as any)?.response?.data?.message ?? 'Could not load the ROC filing.'}</div>
        <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={refetch}>Retry</button>
      </div>
    );
  }
  if (!sheet) return null;

  return (
    <div style={styles.root}>
      {/* Header: what the filing covers and how much PRIMA filled */}
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <div style={styles.title}>ROC Filing — AOC-4 (non-XBRL)</div>
          <div style={styles.muted}>Status: {sheet.filingStatus}{sheet.updatedByName ? ` · last saved by ${sheet.updatedByName}` : ''}</div>
        </div>
        <div style={styles.chips}>
          {sheet.forms.map(f => (
            <div key={f.code} style={styles.chip} title={f.title}>
              <strong>{f.code}</strong>
              <span>online {f.onlineResolved}/{f.onlineFields}</span>
              <span>workbook {f.offlineResolved}/{f.offlineFields}</span>
              {f.lastFilledExcelVersion && <span style={styles.muted}>filled v{f.lastFilledExcelVersion}</span>}
            </div>
          ))}
        </div>
        {sheet.warnings.length > 0 && (
          <div style={styles.warning}>
            {sheet.warnings.map(w => <div key={w}>{w}</div>)}
          </div>
        )}
        <div style={styles.toolbar}>
          <div style={styles.tabs}>
            {([['online', '1. Before download'], ['offline', '2. Workbook fields'], ['fill', '3. Fill workbooks']] as Array<[View, string]>)
              .map(([v, label]) => (
                <button key={v} onClick={() => setView(v)} style={{ ...styles.tab, ...(view === v ? styles.tabActive : {}) }}>{label}</button>
              ))}
          </div>
          {view !== 'fill' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select className="form-select" value={filter} onChange={e => setFilter(e.target.value as Filter)} style={{ width: 170 }}>
                <option value="all">All fields</option>
                <option value="blank">Needs a value</option>
                <option value="typed">Typed / overridden</option>
              </select>
              <button className="btn btn-primary" disabled={dirtyCount === 0 || saving} onClick={() => setConfirmSave(true)}>
                <IconSystem.save size={14} /> Save{dirtyCount > 0 ? ` (${dirtyCount})` : ''}
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={styles.body}>
        {view === 'online' && (
          <div style={styles.hint}>
            Enter these on the MCA V3 portal when you start the filing in <strong>offline mode</strong>, before you download
            the workbooks. Counts and yes/no answers here decide which workbooks the portal issues and how many rows they
            have — check them against PRIMA before downloading.
          </div>
        )}
        {view === 'offline' && (
          <div style={styles.hint}>
            These go into the downloaded workbooks. PRIMA fills them in step 3; type a value to fill a gap or to correct
            PRIMA's. Amounts are full rupees. Previous-year figures the portal pre-filled are kept as MCA has them.
          </div>
        )}

        {view !== 'fill' && grouped.length === 0 && <div style={styles.center}>Nothing to show for this filter.</div>}

        {view !== 'fill' && grouped.map(form => (
          <div key={form.form} style={{ marginBottom: 18 }}>
            <div style={styles.formTitle}>{form.title}</div>
            {form.groups.map(group => {
              const gk = `${view}|${form.form}|${group.name}`;
              const isCollapsed = collapsed[gk] ?? false;
              return (
                <div key={gk} style={styles.group}>
                  <div style={styles.groupHeader} onClick={() => setCollapsed(c => ({ ...c, [gk]: !isCollapsed }))}
                       role="button" tabIndex={0}
                       onKeyDown={e => { if (e.key === 'Enter') setCollapsed(c => ({ ...c, [gk]: !isCollapsed })); }}>
                    <span>{isCollapsed ? '▸' : '▾'} {group.name}</span>
                    <span style={styles.muted}>{group.fields.filter(hasValue).length}/{group.fields.length}</span>
                  </div>
                  {!isCollapsed && group.fields.map(f => (
                    <FieldRow key={keyOf(f)} field={f} draft={drafts[keyOf(f)]} onChange={p => setDraft(f, p)} onCopy={copy} />
                  ))}
                </div>
              );
            })}
          </div>
        ))}

        {view === 'fill' && (
          <div style={{ maxWidth: 760 }}>
            <ol style={styles.steps}>
              <li>On the portal, start AOC-4 in <strong>offline mode</strong> with the answers from step 1, and download the ZIP of workbooks.</li>
              <li>Choose that ZIP (or its .xlsx files) below and click <strong>Fill workbooks</strong>. PRIMA writes only the input cells and returns the same files, plus <em>PRIMA-fill-report.txt</em>.</li>
              <li>Read the report. Open <strong>each</strong> workbook in Excel 2021 or later, review it, and <strong>save</strong> it — saving refreshes the values the portal reads.</li>
              <li>Upload the saved workbooks on the portal. If it reports errors, fix them here or in Excel; the portal issues a new version on every submit — always fill the latest download.</li>
            </ol>
            <div style={styles.uploadBox}>
              <input ref={fileInput} type="file" accept=".zip,.xlsx" multiple
                     onChange={e => setFiles(Array.from(e.target.files ?? []))} />
              <button className="btn btn-primary" disabled={files.length === 0 || filling} onClick={handleFill}>
                <IconSystem.download size={14} /> {filling ? 'Filling…' : 'Fill workbooks'}
              </button>
            </div>
            {dirtyCount > 0 && (
              <div style={styles.warning}>You have {dirtyCount} unsaved value(s) — save them first, or they will not be filled.</div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmSave}
        title="Save ROC filing values"
        message={`Save ${dirtyCount} typed value(s)? Overrides replace PRIMA's value on the forms until cleared.`}
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={saving}
        loadingMessage="Saving…"
        onConfirm={handleSave}
        onCancel={() => setConfirmSave(false)}
      />
    </div>
  );
};

// ── One field ────────────────────────────────────────────────────────────

const FieldRow: React.FC<{
  field: McaSheetField;
  draft: McaManualValue | undefined;
  onChange: (patch: McaManualValue) => void;
  onCopy: (text: string | null | undefined) => void;
}> = ({ field: f, draft, onChange, onCopy }) => {
  const typed = draft ? draft.value ?? null : f.manualValue;
  const shown = typed ?? f.primaValue;
  const isSection = f.sectionName != null;
  const items = draft?.items ?? f.manualItems ?? f.primaItems ?? [];
  const missing = !hasValue(f) && !draft;

  return (
    <div style={{ ...styles.row, ...(missing ? styles.rowMissing : {}) }}>
      <div style={styles.label}>
        <div>
          {f.label ?? f.target}
          {f.period && <span style={styles.period}>{f.period === 'PY' ? 'Previous year' : 'Current year'}</span>}
        </div>
        {(f.remarks || f.sourceNote) && (
          <div style={styles.note}>{[f.remarks, f.sourceNote].filter(Boolean).join(' ')}</div>
        )}
      </div>
      <div style={styles.source}>
        <span style={{ ...styles.badge, ...(f.source === 'MANUAL' || f.source === 'ERROR' ? styles.badgeManual : {}) }}
              title={f.source}>
          {typed != null ? 'Typed' : SOURCE_LABEL[f.source] ?? f.source}
        </span>
      </div>
      <div style={styles.value}>
        {isSection ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {items.length === 0 && <span style={styles.muted}>—</span>}
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={styles.muted}>{i + 1}.</span>
                <input className="form-input" value={item ?? ''} placeholder={f.primaItems?.[i] ?? ''}
                       onChange={e => {
                         const next = [...items];
                         next[i] = e.target.value || null;
                         onChange({ items: next });
                       }} />
                <CopyButton text={item} onCopy={onCopy} />
              </div>
            ))}
          </div>
        ) : f.valueType === 'DROPDOWN' ? (
          <div style={{ display: 'flex', gap: 6 }}>
            <select className="form-select" value={shown ?? ''} onChange={e => onChange({ value: e.target.value || null })}>
              <option value="">—</option>
              {(f.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <CopyButton text={shown} onCopy={onCopy} />
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6 }}>
            <input className="form-input" value={typed ?? ''}
                   placeholder={f.primaValue ?? (f.valueType === 'DATE' ? 'DD/MM/YYYY' : '')}
                   style={{ textAlign: f.valueType === 'AMOUNT' || f.valueType === 'NUMBER' ? 'right' : 'left' }}
                   onChange={e => onChange({ value: e.target.value === '' ? null : e.target.value })} />
            <CopyButton text={shown} onCopy={onCopy} />
          </div>
        )}
      </div>
    </div>
  );
};

const CopyButton: React.FC<{ text: string | null | undefined; onCopy: (t: string | null | undefined) => void }> = ({ text, onCopy }) => (
  <button className="btn btn-sm btn-light" disabled={!text} title="Copy" onClick={() => onCopy(text)} style={{ padding: '2px 6px' }}>
    <IconSystem.copy size={13} />
  </button>
);

const styles: Record<string, React.CSSProperties> = {
  root: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: 'var(--bg-primary)', color: 'var(--text-primary)' },
  header: { padding: '10px 16px', borderBottom: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 8 },
  title: { fontSize: 16, fontWeight: 600 },
  muted: { color: 'var(--text-muted)', fontSize: 12 },
  chips: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  chip: { display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12, padding: '3px 8px', border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-secondary)' },
  warning: { fontSize: 12, padding: '6px 10px', borderRadius: 6, background: 'rgba(217,119,6,0.12)', color: 'var(--text-primary)', border: '1px solid rgba(217,119,6,0.4)' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  tabs: { display: 'flex', gap: 4 },
  tab: { padding: '5px 12px', border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 13 },
  tabActive: { background: '#2563eb', color: '#fff', borderColor: '#2563eb' },
  body: { flex: 1, overflow: 'auto', padding: '12px 16px', minHeight: 0 },
  hint: { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12, maxWidth: 900 },
  center: { padding: 32, textAlign: 'center', color: 'var(--text-secondary)' },
  formTitle: { fontWeight: 600, fontSize: 14, margin: '6px 0' },
  group: { border: '1px solid var(--border-color)', borderRadius: 6, marginBottom: 8, overflow: 'hidden' },
  groupHeader: { display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'var(--grid-header-bg, #dbeafe)', cursor: 'pointer', fontSize: 13, fontWeight: 500, userSelect: 'none' },
  row: { display: 'grid', gridTemplateColumns: 'minmax(240px, 1fr) 70px minmax(220px, 340px)', gap: 10, alignItems: 'start', padding: '6px 10px', borderTop: '1px solid var(--border-color)', fontSize: 13 },
  rowMissing: { background: 'rgba(239,68,68,0.05)' },
  label: { display: 'flex', flexDirection: 'column', gap: 2 },
  note: { fontSize: 11, color: 'var(--text-muted)' },
  period: { marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 4, background: 'var(--bg-secondary)', color: 'var(--text-secondary)' },
  source: { paddingTop: 4 },
  badge: { fontSize: 10, padding: '2px 6px', borderRadius: 10, background: 'rgba(22,163,74,0.14)', color: 'var(--text-primary)' },
  badgeManual: { background: 'rgba(239,68,68,0.14)' },
  value: {},
  steps: { fontSize: 13, lineHeight: 1.6, paddingLeft: 18 },
  uploadBox: { display: 'flex', gap: 12, alignItems: 'center', padding: 12, border: '1px dashed var(--form-border-color)', borderRadius: 8, margin: '12px 0' },
};

export default FsaMcaFilingPane;
